// Master Performance Optimization System
import { preloadCriticalRoutes, cleanupMemory } from './performanceOptimizer';
import { preloadAdminPages, preloadMarketplace, preloadTableComponents } from './preloadStrategies';
import { monitorMemoryUsage, createDebouncedFunction, isLowEndDevice, getNetworkQuality } from './bundleOptimization';

// Performance optimization configuration
export const PERFORMANCE_CONFIG = {
  // Cache settings
  CACHE_TTL: {
    SHORT: 2 * 60 * 1000,    // 2 minutes
    MEDIUM: 5 * 60 * 1000,   // 5 minutes
    LONG: 15 * 60 * 1000,    // 15 minutes
    EXTENDED: 60 * 60 * 1000 // 1 hour
  },
  
  // Query optimization
  BATCH_SIZE: 50,
  MAX_RETRIES: 3,
  DEBOUNCE_DELAY: 300,
  
  // Preloading
  PRELOAD_DELAY: 1000,
  CRITICAL_ROUTE_DELAY: 500,
  
  // Memory management
  MEMORY_CLEANUP_INTERVAL: 5 * 60 * 1000, // 5 minutes
  MEMORY_THRESHOLD: 0.8, // 80% memory usage threshold
  
  // Network adaptation
  SLOW_NETWORK_BATCH_SIZE: 20,
  FAST_NETWORK_BATCH_SIZE: 100,
};

// Performance state management
class PerformanceManager {
  private isInitialized = false;
  private cleanupInterval: NodeJS.Timeout | null = null;
  private performanceObserver: PerformanceObserver | null = null;
  private memoryWarningShown = false;

  // Initialize the performance system
  initialize() {
    if (this.isInitialized) return;
    
    console.log('[PerformanceManager] Initializing performance optimization system...');
    
    // Set up memory monitoring
    this.setupMemoryMonitoring();
    
    // Set up performance monitoring
    this.setupPerformanceMonitoring();
    
    // Set up cleanup intervals
    this.setupCleanupInterval();
    
    // Preload critical resources based on device capabilities
    this.initializePreloading();
    
    // Set up network quality adaptation
    this.adaptToNetworkQuality();
    
    this.isInitialized = true;
    console.log('[PerformanceManager] Performance system initialized');
  }

  // Set up memory monitoring and cleanup
  private setupMemoryMonitoring() {
    if (typeof window === 'undefined') return;
    
    const checkMemory = () => {
      const memoryInfo = monitorMemoryUsage();
      if (memoryInfo && memoryInfo.percentage > PERFORMANCE_CONFIG.MEMORY_THRESHOLD) {
        if (!this.memoryWarningShown) {
          console.warn(`[PerformanceManager] High memory usage: ${memoryInfo.percentage.toFixed(1)}%`);
          this.memoryWarningShown = true;
          
          // Trigger aggressive cleanup
          this.performAggressiveCleanup();
        }
      } else {
        this.memoryWarningShown = false;
      }
    };

    // Check memory every 30 seconds
    setInterval(checkMemory, 30000);
  }

  // Set up performance monitoring
  private setupPerformanceMonitoring() {
    if (typeof window === 'undefined' || !('PerformanceObserver' in window)) return;

    try {
      this.performanceObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (entry.entryType === 'navigation') {
            const navigation = entry as PerformanceNavigationTiming;
            console.log(`[PerformanceManager] Page load time: ${navigation.loadEventEnd - navigation.fetchStart}ms`);
          }
          
          if (entry.entryType === 'largest-contentful-paint') {
            console.log(`[PerformanceManager] LCP: ${entry.startTime}ms`);
          }
        });
      });

      this.performanceObserver.observe({ 
        entryTypes: ['navigation', 'largest-contentful-paint', 'first-input'] 
      });
    } catch (error) {
      console.warn('[PerformanceManager] Performance monitoring not available:', error);
    }
  }

  // Set up regular cleanup intervals
  private setupCleanupInterval() {
    this.cleanupInterval = setInterval(() => {
      console.log('[PerformanceManager] Running scheduled cleanup...');
      cleanupMemory();
    }, PERFORMANCE_CONFIG.MEMORY_CLEANUP_INTERVAL);
  }

  // Initialize preloading based on device capabilities
  private initializePreloading() {
    const isLowEnd = isLowEndDevice();
    
    if (isLowEnd) {
      console.log('[PerformanceManager] Low-end device detected, using conservative preloading');
      // Delay preloading for low-end devices
      setTimeout(() => {
        preloadCriticalRoutes();
      }, PERFORMANCE_CONFIG.PRELOAD_DELAY * 2);
    } else {
      console.log('[PerformanceManager] High-end device detected, using aggressive preloading');
      // Immediate preloading for high-end devices
      setTimeout(() => {
        preloadCriticalRoutes();
        preloadTableComponents();
      }, PERFORMANCE_CONFIG.CRITICAL_ROUTE_DELAY);
    }
  }

  // Adapt behavior to network quality
  private adaptToNetworkQuality() {
    const networkQuality = getNetworkQuality();
    
    switch (networkQuality) {
      case 'slow':
        console.log('[PerformanceManager] Slow network detected, optimizing for bandwidth');
        // Reduce batch sizes, increase cache times
        break;
      case 'fast':
        console.log('[PerformanceManager] Fast network detected, enabling aggressive preloading');
        // Larger batch sizes, preload more aggressively
        break;
      default:
        console.log('[PerformanceManager] Unknown network quality, using default settings');
    }
  }

  // Perform aggressive cleanup when memory is high
  private performAggressiveCleanup() {
    console.log('[PerformanceManager] Performing aggressive cleanup...');
    
    // Clear all caches
    cleanupMemory();
    
    // Force garbage collection if available
    if (process.env.NODE_ENV === 'development' && 'gc' in window) {
      (window as any).gc();
    }
    
    // Clear unused images
    this.clearUnusedImages();
  }

  // Clear unused images from memory
  private clearUnusedImages() {
    const images = document.getElementsByTagName('img');
    for (let i = 0; i < images.length; i++) {
      const img = images[i];
      if (!this.isImageVisible(img)) {
        // Clear src to free memory
        const originalSrc = img.src;
        img.removeAttribute('src');
        img.dataset.originalSrc = originalSrc;
      }
    }
  }

  // Check if image is visible in viewport
  private isImageVisible(img: HTMLImageElement): boolean {
    const rect = img.getBoundingClientRect();
    const windowHeight = window.innerHeight || document.documentElement.clientHeight;
    const windowWidth = window.innerWidth || document.documentElement.clientWidth;
    
    return (
      rect.top < windowHeight &&
      rect.bottom > 0 &&
      rect.left < windowWidth &&
      rect.right > 0
    );
  }

  // Preload route-specific resources
  preloadRouteResources(routeName: string) {
    const debouncedPreload = createDebouncedFunction(() => {
      switch (routeName) {
        case 'admin':
          preloadAdminPages();
          break;
        case 'marketplace':
          preloadMarketplace();
          break;
        case 'tables':
          preloadTableComponents();
          break;
        default:
          // Default preloading
          break;
      }
    }, PERFORMANCE_CONFIG.DEBOUNCE_DELAY);

    debouncedPreload();
  }

  // Get performance metrics
  getPerformanceMetrics() {
    const memoryInfo = monitorMemoryUsage();
    const networkQuality = getNetworkQuality();
    const isLowEnd = isLowEndDevice();

    return {
      memory: memoryInfo,
      network: networkQuality,
      deviceType: isLowEnd ? 'low-end' : 'high-end',
      cacheStatus: 'active', // Would be connected to actual cache metrics
      lastCleanup: Date.now(),
    };
  }

  // Cleanup when component unmounts or page unloads
  cleanup() {
    console.log('[PerformanceManager] Cleaning up performance system...');
    
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }

    if (this.performanceObserver) {
      this.performanceObserver.disconnect();
      this.performanceObserver = null;
    }

    cleanupMemory();
    this.isInitialized = false;
  }
}

// Create singleton instance
export const performanceManager = new PerformanceManager();

// Auto-initialize on import (for browser environment)
if (typeof window !== 'undefined') {
  // Initialize after page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      performanceManager.initialize();
    });
  } else {
    performanceManager.initialize();
  }

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    performanceManager.cleanup();
  });
}

// Export convenience functions
export const initializePerformanceSystem = () => performanceManager.initialize();
export const preloadForRoute = (route: string) => performanceManager.preloadRouteResources(route);
export const getPerformanceStatus = () => performanceManager.getPerformanceMetrics();
export const cleanupPerformanceSystem = () => performanceManager.cleanup();